import { GridRenderCellParams } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";
import { AllowedTableColumn } from "../../../../utils/types/AllowTableTypes";
import {
  GfFigmaTemplateData,
  GfFlyerItemCategory,
} from "@swagger/typescript-fetch-goflyer";
import { RenderEditButton } from "../../../components/RenderEditButtonTable";
import { PermissionComponentWrapper } from "../../../components/PermissionComponentWrapper";
import { useNavigate } from "react-router-dom";
import { RenderMultiImageTable } from "../../../components/RenderImageTable";
import DownloadIcon from "@mui/icons-material/Download";
import { IconButton, Tooltip } from "@mui/material";
import { makeStoreDisplayName } from "../../../../utils/dtoUtil/makeStoreDisplayName";
import { routes } from "../../../../utils/constant/navigationRoutes";

export const useFigmaTemplateDataTableColumns = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dateCols: AllowedTableColumn[] = ["updatedAt", "createdAt"];

  const handleOnClickEdit = (row: GfFigmaTemplateData) => {
    if (row?.id) {
      navigate(routes.figmaTemplate.editById(row.id));
    }
  };

  const handleDownloadFigmaFile = (url: string, name: string) => {
    if (!url) return;

    const link = document.createElement("a");
    link.href = url;
    link.download = `${name || "figma-template"}.fig`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      field: "edit",
      width: 90,
      headerName: `${t(messages.Edit())}`,
      filterable: false,
      renderCell: (props: GridRenderCellParams) => {
        return (
          <PermissionComponentWrapper
            requiredPermission={["edit"]}
            requiredPermissionGroup={["figmaTemplates"]}
          >
            <RenderEditButton onClick={handleOnClickEdit} {...props} />
          </PermissionComponentWrapper>
        );
      },
      sortable: false,
    },
    {
      field: "image",
      headerName: `Preview`,
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GfFigmaTemplateData>) => {
        return params.row.image ? (
          <RenderMultiImageTable images={[params.row.image]} />
        ) : (
          <span>-</span>
        );
      },
    },
    {
      field: "name",
      headerName: `${t(messages["name"] ? messages["name"]() : "Name")}`,
      width: 220,
    },
    {
      field: "numberOfPages",
      headerName: ` Pages`,
      width: 100,
      valueGetter: (params: GridRenderCellParams<GfFigmaTemplateData>) =>
        params.row.numberOfPages || 0,
    },
    {
      field: "figmaTemplateTag",
      headerName: `${t(messages["Tag"] ? messages["Tag"]() : "Tag")}`,
      width: 150,
      sortable: true,
      valueGetter: (params: GridRenderCellParams<GfFigmaTemplateData>) =>
        params.row.figmaTemplateTag?.name || "-",
    },
    {
      field: "type",
      headerName: "Type",
      width: 120,
      sortable: true,
      renderCell: (params: GridRenderCellParams<GfFigmaTemplateData>) => {
        return <span>{params.row?.type ?? "-"}</span>;
      },
    },
    {
      field: "category",
      headerName: `${t(
        messages["Category"] ? messages["Category"]() : "Category",
      )}`,
      width: 200,
      sortable: true,
      renderCell: (params: GridRenderCellParams<GfFigmaTemplateData>) => {
        const categories = params.row?.category as GfFlyerItemCategory[];
        return (
          <span>{categories?.map((cat) => cat.name).join(", ") || "-"}</span>
        );
      },
    },

    {
      field: "stores",
      headerName: `${t(messages["Stores"] ? messages["Stores"]() : "Stores")}`,
      width: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfFigmaTemplateData>) => {
        const store = params.row?.stores?.[0];

        if (!store) return <></>;
        const storeName = makeStoreDisplayName(store);
        return <div title={storeName?.trim()}>{storeName}</div>;
      },
    },
    {
      field: "figmaTemplateFiles",
      headerName: `Figma File`,
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GfFigmaTemplateData>) => {
        const url = params.row.figmaTemplateFiles?.[0]?.src;
        return url ? (
          <Tooltip title="Download Figma File">
            <IconButton
              onClick={() => {
                url && handleDownloadFigmaFile(url, params.row.name);
              }}
              color="primary"
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <span>-</span>
        );
      },
    },
    ...dateCols.map((col) => ({
      field: col,
      headerName: `${t(messages[col] ? messages[col]() : col)}`,
      width: 200,
      type: "dateTime",
      valueGetter: (val: GridRenderCellParams) =>
        val && (val.row as any)[col]
          ? new Date((val.row as any)[col])
          : undefined,
      sortable: true,
    })),
    {
      field: "CreatedBy",
      width: 200,
      headerName: `${t(messages["CreatedBy"]())}`,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GfFigmaTemplateData>) => {
        const { firstName, lastName, userName, email } =
          params?.row?.adminUser || {};
        const createdBy = firstName
          ? `${firstName} ${lastName || ""}`
          : userName || email || "";

        return <div title={createdBy.trim()}>{createdBy}</div>;
      },
    },
    {
      field: "id",
      headerName: `${t(messages["id"] ? messages["id"]() : "ID")}`,
      width: 300,
      filterable: false,
    },
  ];

  return columns;
};
